/**
 * Unified Error Service
 *
 * Centralizes error message extraction, translation, and display
 * across the entire application. Replaces scattered error handling patterns.
 */

import { defaultToast } from '../utils/toast';
import { logger } from '../utils/logger';
import type { ApiError, ToastOptions } from '../../types/api';

// ============================================
// Backend Error Message Translation Map
// ============================================
const BACKEND_ERROR_MAP: Record<string, string> = {
  // Tenant Validation Errors
  'Tenant code must be alphanumeric with hyphens only':
    '테넌트 코드는 영문자, 숫자, 하이픈(-)만 사용 가능합니다.',
  'Tenant code must be lowercase alphanumeric with hyphens only':
    '테넌트 코드는 소문자, 숫자, 하이픈(-)만 사용 가능합니다.',
  'Tenant code is required': '테넌트 코드를 입력해 주세요.',
  'Tenant name is required': '테넌트 이름을 입력해 주세요.',
  'Invalid email format': '올바르지 않은 이메일 형식입니다.',
  'Tenant not found': '테넌트를 찾을 수 없습니다.',
  'Tenant code already exists': '이미 존재하는 테넌트 코드입니다.',

  // Channel Validation Errors
  'Channel code is required': '채널 코드를 입력해 주세요.',
  'Channel type is required': '채널 유형을 선택해 주세요.',
  'Channel not found': '채널을 찾을 수 없습니다.',
  'Channel code already exists': '이미 존재하는 채널 코드입니다.',
  'Invalid state transition': '유효하지 않은 상태 전환입니다.',

  // User Validation Errors
  'Email is required': '이메일을 입력해 주세요.',
  'Email already exists': '이미 존재하는 이메일입니다.',
  'Password is required': '비밀번호를 입력해 주세요.',
  'Password must be at least 8 characters': '비밀번호는 8자 이상이어야 합니다.',
  'Name is required': '이름을 입력해 주세요.',
  'Role is required': '역할을 선택해 주세요.',
  'User not found': '사용자를 찾을 수 없습니다.',
  'Cannot delete the last global admin': '마지막 글로벌 관리자는 삭제할 수 없습니다.',

  // Variable Errors
  'in use': '사용 중인 항목이므로 삭제할 수 없습니다.',

  // LLM Model Errors
  associated: '연결된 설정이 있어 삭제할 수 없습니다.',
  referenced: '참조 중인 항목이 있어 삭제할 수 없습니다.',
  configuration: '설정에서 사용 중이므로 삭제할 수 없습니다.',

  // Generic Errors
  'Internal server error': '서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
  Unauthorized: '인증이 필요합니다. 다시 로그인해 주세요.',
  Forbidden: '접근 권한이 없습니다.',
  'Bad request': '잘못된 요청입니다.',
  'Not found': '요청한 리소스를 찾을 수 없습니다.',
};

// HTTP status code → Korean message
const STATUS_MESSAGES: Record<number, string> = {
  400: '잘못된 요청입니다. 입력 내용을 확인해 주세요.',
  401: '인증이 필요합니다. 다시 로그인해 주세요.',
  403: '접근 권한이 없습니다.',
  404: '요청한 리소스를 찾을 수 없습니다.',
  409: '이미 존재하는 데이터와 충돌이 발생했습니다.',
  429: '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.',
  500: '서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
  502: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.',
  503: '서비스가 일시적으로 중단되었습니다. 잠시 후 다시 시도해 주세요.',
};

/**
 * Extract a user-friendly Korean error message from an error object.
 *
 * Priority: error.data.message → error.message → status code → fallback
 *
 * @param error - Error object (may have .status, .data properties)
 * @param fallbackMessage - Default message if nothing matches
 * @returns User-friendly Korean error message
 */
export function extractErrorMessage(
  error: ApiError | Error | null | undefined,
  fallbackMessage: string = '오류가 발생했습니다.'
): string {
  if (!error) return fallbackMessage;

  // Get the raw message from the error
  const apiError = error as ApiError;
  const rawMessage: string = apiError.data?.message || error.message || '';

  if (!rawMessage && !apiError.status) return fallbackMessage;

  // Try translation of the raw message
  if (rawMessage) {
    const translated = translateErrorMessage(rawMessage, null);
    if (translated) return translated;
  }

  // Try status code based message
  if (apiError.status && STATUS_MESSAGES[apiError.status]) {
    return STATUS_MESSAGES[apiError.status];
  }

  return fallbackMessage;
}

/**
 * Translate a backend error message to user-friendly Korean.
 *
 * @param errorMessage - Raw error message
 * @param fallback - Fallback (null = return null if no match)
 * @returns Translated message or fallback
 */
export function translateErrorMessage(
  errorMessage: string | null | undefined,
  fallback: string | null = '오류가 발생했습니다.'
): string | null {
  if (!errorMessage) return fallback;

  // Exact match
  if (BACKEND_ERROR_MAP[errorMessage]) {
    return BACKEND_ERROR_MAP[errorMessage];
  }

  // Partial match (case-insensitive)
  const lowerMessage = errorMessage.toLowerCase();
  for (const [key, value] of Object.entries(BACKEND_ERROR_MAP)) {
    if (lowerMessage.includes(key.toLowerCase())) {
      return value;
    }
  }

  // HTTP status code pattern matching (from legacy strings like "HTTP 404")
  if (errorMessage.startsWith('HTTP 4')) {
    if (errorMessage.includes('400')) return STATUS_MESSAGES[400];
    if (errorMessage.includes('401')) return STATUS_MESSAGES[401];
    if (errorMessage.includes('403')) return STATUS_MESSAGES[403];
    if (errorMessage.includes('404')) return STATUS_MESSAGES[404];
  }
  if (errorMessage.startsWith('HTTP 5')) {
    return STATUS_MESSAGES[500];
  }

  // Hide overly technical messages
  if (errorMessage.length > 100 || /exception|stack|trace/i.test(errorMessage)) {
    return fallback;
  }

  return fallback;
}

/**
 * Show an error toast with a user-friendly message extracted from the error.
 *
 * @param error - Error object
 * @param contextMessage - Optional context prefix (e.g., "저장에 실패했습니다.")
 */
export function showError(
  error: ApiError | Error | null | undefined,
  contextMessage?: string
): void {
  const message = contextMessage || extractErrorMessage(error);
  logger.error('[ErrorService]', error);
  defaultToast.error(message);
}

/**
 * Show a warning toast.
 *
 * @param message - Warning message
 */
export function showWarning(message: string): void {
  defaultToast.warning(message);
}

/**
 * Check if an error is an authentication error (401/403).
 *
 * @param error - Error object
 */
export function isAuthError(error: ApiError | Error | null | undefined): boolean {
  return (error as ApiError)?.status === 401 || (error as ApiError)?.status === 403;
}

/**
 * Check if an error matches a specific HTTP status code.
 *
 * @param error - Error object
 * @param status - HTTP status code
 */
export function isStatus(error: ApiError | Error | null | undefined, status: number): boolean {
  return (error as ApiError)?.status === status;
}

/**
 * Show a rate limit error toast with countdown timer.
 *
 * @param retryAfterSec - Seconds until retry is allowed (default: 60)
 */
export function showRateLimitError(retryAfterSec: number = 60): void {
  const seconds = Math.max(1, Math.round(retryAfterSec));
  const options: ToastOptions = { autoClose: seconds * 1000 };
  defaultToast.error(`요청 한도를 초과했습니다. ${seconds}초 후 다시 시도해 주세요.`, options);
}
